import React, { useEffect, useState } from "react";
import { eyeCameraCfg, eyeLauncher, tvWakeLauncher } from "../lib/api";
import { Camera, Laptop, ChevronDown, Copy, Download } from "lucide-react";
import { toast } from "sonner";

export const DEFAULT_BRIDGE = "http://localhost:1984/api/stream.mp4?src=tapo";

const CONFIG = `streams:
  tapo: rtsp://CAMERA_USER:CAMERA_PASS@CAMERA_IP:554/stream1

api:
  listen: ":1984"
  origin: "*"`;

const dl = (os) => eyeLauncher(os).then((blob) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = os === "mac" ? "NexusEye.command" : "NexusEye.bat"; a.click();
  toast.success("Downloaded. Double-click it, then click Yes when Windows asks for admin.");
}).catch(() => toast.error("Could not build the launcher"));

export const LauncherButtons = () => (
  <div className="space-y-2" data-testid="eye-launcher">
    <p className="text-xs text-cyan-100/80">One click: downloads the camera bridge, connects your Tapo, keeps this PC awake 24/7, starts at boot and opens Nexus's eye.</p>
    <div className="grid grid-cols-2 gap-2">
      <button onClick={() => dl("windows")} data-testid="eye-launcher-windows" className="flex items-center justify-center gap-2 rounded-xl py-3 bg-[#00F0FF] text-[#030712] font-bold text-sm"><Download className="w-4 h-4" /> Windows PC</button>
      <button onClick={() => dl("mac")} data-testid="eye-launcher-mac" className="flex items-center justify-center gap-2 rounded-xl py-3 bg-white/10 border border-cyan-300/20 text-cyan-50 font-bold text-sm"><Download className="w-4 h-4" /> Mac</button>
    </div>
    <p className="text-[11px] text-cyan-200/50">Windows may say "Windows protected your PC": click More info → Run anyway. Don't paste anything into cmd.</p>
    <TvWakeButtons />
  </div>
);

const dlWake = (os) => tvWakeLauncher(os).then((blob) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = os === "mac" ? "NexusTvWake.command" : "NexusTvWake.bat"; a.click();
  toast.success("Downloaded. Run it on the office PC and type the Fire Stick's IP when asked.");
}).catch(() => toast.error("Could not build the TV wake agent"));

const TvWakeButtons = () => (
  <div className="mt-3 pt-3 border-t border-cyan-300/10 space-y-2" data-testid="eye-tv-wake">
    <p className="text-xs font-bold text-cyan-50">Office TV auto-on (onn. Google TV stick)</p>
    <p className="text-xs text-cyan-100/80">When the camera recognises you (live ✓), the office PC wakes up to 2 onn. sticks, the TVs turn on and open their Nexus screen (e.g. TV A + TV B). Nobody seen for 30 min → TVs turn off. Run it on the same PC as the camera bridge.</p>
    <ol className="list-decimal pl-5 text-[11px] text-cyan-200/70 space-y-0.5">
      <li>onn. stick: Settings → System → About → click <b>Android TV OS build</b> 7 times. Then Settings → System → Developer options → <b>USB debugging ON</b>.</li>
      <li>onn. stick: Settings → Display &amp; Sound → <b>HDMI-CEC → Turn on TV ON</b>. Insignia TV: Settings → System → <b>INlink / CEC ON</b>.</li>
      <li>Note the IP: Settings → Network &amp; Internet → your Wi-Fi. Run the file below, enter the IP, and on the TV tick <b>Always allow</b> → OK.</li>
      <li>The stick needs a browser app (e.g. TV Bro) — it opens TV A in it.</li>
    </ol>
    <div className="grid grid-cols-2 gap-2">
      <button onClick={() => dlWake("windows")} data-testid="eye-tv-wake-windows" className="flex items-center justify-center gap-2 rounded-xl py-3 bg-[#00F0FF] text-[#030712] font-bold text-sm"><Download className="w-4 h-4" /> TV wake · Windows</button>
      <button onClick={() => dlWake("mac")} data-testid="eye-tv-wake-mac" className="flex items-center justify-center gap-2 rounded-xl py-3 bg-white/10 border border-cyan-300/20 text-cyan-50 font-bold text-sm"><Download className="w-4 h-4" /> TV wake · Mac</button>
    </div>
  </div>
);

const Guide = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { eyeCameraCfg().then((d) => d.configured && setCfg(d)).catch(() => {}); }, []);
  const text = cfg?.config || CONFIG;
  return (
  <ol className="list-decimal pl-5 space-y-1.5 text-xs text-cyan-100/80" data-testid="eye-tapo-guide">
    <li>Tapo app → your C210 → Settings → Advanced Settings → <b>Camera Account</b>: create a username + password (6–32 chars).</li>
    <li>In your router, give the camera a <b>fixed IP</b> (DHCP reservation).</li>
    <li>On this PC, download <b>go2rtc</b> (github.com/AlexxIT/go2rtc → Releases) and put <code>go2rtc.yaml</code> next to it:</li>
    <li className="list-none -ml-5">
      <pre className="bg-[#030712] border border-cyan-300/20 rounded-lg p-2 text-[11px] text-emerald-200 overflow-x-auto" data-testid="eye-tapo-config">{text}</pre>
      <button onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Config copied"))} data-testid="eye-tapo-copy-config"
        className="mt-1 flex items-center gap-1 text-[11px] text-cyan-300"><Copy className="w-3 h-3" /> Copy config</button>
    </li>
    <li>{cfg ? <>Your camera ({cfg.ip}) is already filled in, so just run go2rtc.</> : "Replace CAMERA_USER / CAMERA_PASS / CAMERA_IP, then run go2rtc."} Check <code>http://localhost:1984</code> shows the camera.</li>
    <li>Open this /eye page <b>on the same PC</b>, choose Tapo C210, then Open Nexus's eye. The password stays on this PC only.</li>
    <li>Tapo limit: only 2 of Tapo Care / SD-card recording / Nexus can run at once.</li>
  </ol>
  );
};

export const CameraSource = ({ source, setSource, bridge, setBridge, locked }) => {
  const [open, setOpen] = useState(false);
  const btn = (id, Icon, label) => (
    <button onClick={() => setSource(id)} disabled={locked} data-testid={`eye-source-${id}`}
      className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2 text-sm font-bold transition-colors disabled:opacity-50 ${source === id ? "bg-[#00F0FF] text-[#030712]" : "border border-cyan-300/25 text-cyan-200/70"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return (
    <div className="w-full max-w-md nx-glass rounded-2xl p-4 space-y-3" data-testid="eye-camera-source">
      <p className="text-xs tracking-[0.3em] text-cyan-300/70 font-bold">CAMERA SOURCE</p>
      <div className="flex gap-2">{btn("webcam", Laptop, "Laptop webcam")}{btn("tapo", Camera, "Tapo C210")}</div>
      {source === "tapo" && (
        <>
          <LauncherButtons />
          <input value={bridge} onChange={(e) => setBridge(e.target.value)} disabled={locked} data-testid="eye-bridge-url"
            className="w-full bg-[#030712] border border-cyan-300/20 rounded-lg px-3 py-2 text-xs nx-mono" />
          <button onClick={() => setOpen((o) => !o)} data-testid="eye-tapo-guide-toggle" className="flex items-center gap-1 text-xs text-cyan-300">
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /> Manual setup (advanced)
          </button>
          {open && <Guide />}
        </>
      )}
    </div>
  );
};
